import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../lib/api';
import { trackEvent } from '../lib/analytics';

interface InquiryFormProps {
  type: 'quote' | 'distributor' | 'contact' | 'callback';
  productSku?: string;
  title?: string;
  description?: string;
}

interface FormValues {
  name: string;
  company: string;
  email: string;
  phone: string;
  city: string;
  message: string;
  honeypot: string;
}

export default function InquiryForm({ type, productSku, title, description }: InquiryFormProps) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormValues>();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = async (values: FormValues) => {
    if (values.honeypot) return; // bot
    try {
      await api.createInquiry({
        type,
        name: values.name,
        company: values.company,
        email: values.email,
        phone: values.phone,
        city: values.city,
        message: values.message,
        productSkus: productSku ? [productSku] : undefined,
        honeypot: values.honeypot,
      });
      trackEvent('inquiry_submit', { inquiry_type: type, product_sku: productSku ?? '' });
      setStatus('success');
      reset();
    } catch (e) {
      setStatus('error');
      setErrorMessage(e instanceof Error ? e.message : 'Submission failed');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-xl border border-accent-green/30 bg-green-50 p-6 text-center">
        <h3 className="font-display text-xl font-semibold text-green-800">Thank you!</h3>
        <p className="mt-2 text-sm text-green-700">
          Your enquiry has been received. Our team will reach out within 1 business day.
        </p>
        <button onClick={() => setStatus('idle')} className="btn-secondary mt-4">
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="card p-6 sm:p-8">
      {title && <h3 className="mb-1 font-display text-xl font-semibold">{title}</h3>}
      {description && <p className="mb-5 text-sm text-slate-600">{description}</p>}

      <input type="text" autoComplete="off" tabIndex={-1} aria-hidden className="hidden" {...register('honeypot')} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Full Name *</label>
          <input className="input" {...register('name', { required: true, minLength: 2 })} />
          {errors.name && <p className="mt-1 text-xs text-red-600">Required</p>}
        </div>
        <div>
          <label className="label">Company / Organisation</label>
          <input className="input" {...register('company')} />
        </div>
        <div>
          <label className="label">Email *</label>
          <input type="email" className="input" {...register('email', { required: true, pattern: /^[^@]+@[^@]+\.[^@]+$/ })} />
          {errors.email && <p className="mt-1 text-xs text-red-600">Valid email required</p>}
        </div>
        <div>
          <label className="label">Phone *</label>
          <input className="input" {...register('phone', { required: true, minLength: 7 })} />
          {errors.phone && <p className="mt-1 text-xs text-red-600">Required</p>}
        </div>
        <div className="sm:col-span-2">
          <label className="label">City</label>
          <input className="input" {...register('city')} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Message *</label>
          <textarea
            rows={4}
            className="input"
            placeholder={
              type === 'distributor'
                ? 'Tell us about your distribution territory, current product lines, and target customers.'
                : type === 'quote'
                ? 'Quantity, delivery location, timeline'
                : 'How can we help?'
            }
            {...register('message', { required: true, minLength: 10 })}
          />
          {errors.message && <p className="mt-1 text-xs text-red-600">Please describe your enquiry (min 10 chars)</p>}
        </div>
      </div>

      {status === 'error' && (
        <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage}</p>
      )}

      <button type="submit" disabled={isSubmitting} className="btn-primary mt-6 w-full sm:w-auto">
        {isSubmitting ? 'Submitting…' : 'Submit Enquiry'}
      </button>
    </form>
  );
}
